import { PaymentMethod, PaymentFrequency, DividendDistributionMethod } from "./api/types/order";
import type { SelectMetadataVO } from "./api/types/common";
import { FamilyMemberRelationship } from "./api/types/order.js";

// 通用字典工具

export type DictValue = string | number | boolean | null | undefined;

export interface DictOption<T = DictValue> {
  label: string;
  value: T;
  color?: string;
  [property: string]: unknown;
}

export type DictMap<T = DictValue> = Record<string, DictOption<T> | DictValue | string>;

// 元數據提供者（依賴注入模式）
export type SelectMetadataProvider = () => SelectMetadataVO[];

let selectMetadataProvider: SelectMetadataProvider | null = null;

/**
 * 設置元數據提供者
 * 應用啟動時調用此方法注入元數據訪問器
 */
export function setSelectMetadataProvider(provider: SelectMetadataProvider | null) {
  selectMetadataProvider = provider;
}

// 國家數據提供者（依賴注入模式）
export interface CountryData {
  id?: number;
  countryCode?: string;
  cname?: string;
  name?: string;
  areaCode?: string;
  [key: string]: any;
}

export type CountryProvider = () => CountryData[];

let countryProvider: CountryProvider | null = null;

/**
 * 設置國家數據提供者
 * 應用啟動時調用此方法注入國家數據訪問器
 */
export function setCountryProvider(provider: CountryProvider | null) {
  countryProvider = provider;
}

/**
 * 獲取國家列表
 */
export function getCountries(): CountryData[] {
  return countryProvider?.() || [];
}

/**
 * 獲取元數據列表
 */
export function getSelectMetadata(): SelectMetadataVO[] {
  return selectMetadataProvider?.() || [];
}

/**
 * 根據 code 獲取指定的元數據項
 * @param code 元數據代碼
 * @returns SelectMetadataVO | undefined
 */
export function getSelectMetadataByCode(code: string): SelectMetadataVO | undefined {
  const metadataList = getSelectMetadata();
  return metadataList.find((item) => item.code === code);
}

/** 將字典數組轉換為 Map 形式，便於 O(1) 查找 */
export function toDictMap<T = DictValue>(options: DictOption<T>[]): DictMap<T> {
  return options.reduce<DictMap<T>>((acc, cur) => {
    acc[String(cur.value)] = cur;
    return acc;
  }, {});
}

/** 獲取字典項，支持傳入數組或 Map */
export function getDictOption<T = DictValue>(dict: DictOption<T>[] | DictMap<T>, value: T): DictOption<T> | DictValue | string | undefined {
  if (!dict) return undefined;
  if (Array.isArray(dict)) {
    return dict.find((item) => item.value === value);
  }
  return dict[String(value)];
}

/** 獲取字典 label，未命中返回 fallback（默認 "-"），支持數組值 */
export function getDictLabel<T = DictValue>(dict: DictOption<T>[] | DictMap<T>, value: T | T[], fallback = "-"): string {
  const resolver = (val: T) => {
    const option = getDictOption(dict, val);
    if (option && typeof option === "object" && "label" in option) {
      return (option as DictOption<T>).label ?? fallback;
    }
    if (option !== null && option !== undefined) {
      return String(option);
    }
    return fallback;
  };

  if (Array.isArray(value)) {
    return value.map((v) => resolver(v)).join("，");
  }
  return resolver(value);
}

// 內置通用字典
export const COMMON_DICTS = {
  gender: [
    { value: "MALE", label: "男" },
    { value: "FEMALE", label: "女" },
    { value: "UNKNOWN", label: "未知" },
  ],
  salutation: [
    { value: "MR", label: "先生" },
    { value: "MRS", label: "太太" },
    { value: "MS", label: "女士" },
    { value: "UNKNOWN", label: "未知" },
  ],
  boolean: [
    { value: true, label: "是" },
    { value: false, label: "否" },
  ],
  idType: [
    { value: "UNKNOWN", label: "待確認" },
    { value: "CHINA_ID", label: "中國身份證" },
    { value: "HONGKONG_ID", label: "香港身份證" },
    { value: "TAIWAN_ID", label: "台灣證件" },
    { value: "PASSPORT", label: "護照" },
    { value: "BIRTH_CERTIFICATE", label: "出生證明" },
    { value: "MACAO_ID", label: "澳門證件" },
    { value: "HK_MACAU_PASS", label: "港澳通行證" },
  ],
  industry: [],
  relationship: [
    { value: "UNKNOWN", label: "待確認" },
    { value: "SELF", label: "本人" },
    { value: "PARENT", label: "父母" },
    { value: "CHILD", label: "子女" },
    { value: "SPOUSE", label: "配偶" },
    { value: "BROTHER", label: "兄弟" },
    { value: "SISTER", label: "姐妹" },
    { value: "BROTHER_AND_SISTER", label: "兄妹" },
    { value: "SISTER_AND_BROTHER", label: "姐弟" },
    { value: "GRANDPARENT_OR_GRANDCHILD", label: "祖孫" },
    { value: "LEGAL_GUARDIAN", label: "監護人" },
    { value: "EMPLOYEE", label: "僱員" },
    { value: "TRUSTEE", label: "受託人" },
    { value: "TRUSTOR", label: "委託人" },
    { value: "FIANCEE", label: "未婚妻" },
    { value: "FIANCE", label: "未婚夫" },
    { value: "OTHER", label: "其他" },
    { value: "UNIDENTIFIED", label: "未識別" },
  ],
  hkEntryIdType: [
    { value: "UNKNOWN", label: "未知" },
    { value: "PASSPORT", label: "護照" },
    { value: "TEMP_PASSPORT", label: "臨時護照" },
  ],
  jobType: [
    { value: "FULL_TIME", label: "全職" },
    { value: "PART_TIME", label: "兼職" },
    { value: "SELF_EMPLOYED", label: "自僱" },
    { value: "UNPAID_PERSON", label: "無薪人士" },
    { value: "UNKNOWN", label: "未知" },
  ],
  phoneCode: [
    { value: "852", label: "+852" },
    { value: "86", label: "+86" },
  ],
  retirementAge: [
    { value: "55", label: "55" },
    { value: "60", label: "60" },
    { value: "65", label: "65" },
  ],
  education: [
    { value: "PRIMARY", label: "小學或以下" },
    { value: "SECONDARY", label: "中學" },
    { value: "UNIVERSITY", label: "大學或以上" },
    { value: "UNKNOWN", label: "未知" },
  ],
  signatureType: [
    { value: "ADVISOR", label: "業務代表" },
    { value: "INSURED", label: "受保人" },
    { value: "POLICYHOLDER", label: "投保人" },
    { value: "WITNESS", label: "見證人" },
  ],
  maritalStatus: [
    { value: "MARRIED", label: "已婚" },
    { value: "SINGLE", label: "單身" },
    { value: "DIVORCED", label: "離婚" },
    { value: "WIDOWED", label: "喪偶" },
    { value: "UNKNOWN", label: "未知" },
  ],
  country: [
    { value: "CN", label: "中國" },
    { value: "HK", label: "香港" },
    { value: "US", label: "美國" },
    { value: "TW", label: "台灣" },
    { value: "MO", label: "澳門" },
  ],
  city: [{ value: "HONG_KONG", label: "香港" }],
  clientType: [
    { value: "INDIVIDUAL", label: "個人" },
    { value: "COMPANY", label: "公司" },
  ],
  currency: [
    { value: "HKD", label: "HKD" },
    { value: "USD", label: "USD" },
    { value: "CNY", label: "CNY" },
    { value: "MOP", label: "MOP" },
    { value: "EUR", label: "EUR" },
    { value: "GBP", label: "GBP" },
    { value: "AUD", label: "AUD" },
    { value: "CAD", label: "CAD" },
    { value: "SGD", label: "SGD" },
    { value: "CHF", label: "CHF" },
    { value: "JPY", label: "JPY" },
    { value: "NZD", label: "NZD" },
  ],
  firstPaymentMethod: [
    { value: PaymentMethod.UNKNOWN, label: "待確認" },
    { value: PaymentMethod.CASH, label: "現金" },
    { value: PaymentMethod.CHEQUE, label: "支票" },
    { value: PaymentMethod.TELEGRAPHIC_TRANSFER, label: "電匯轉賬" },
    { value: PaymentMethod.CASH_ORDER, label: "銀行本票" },
    { value: PaymentMethod.BANK_TRANSFER, label: "銀行轉賬" },
    { value: PaymentMethod.CREDIT_CARD, label: "信用卡" },
    { value: PaymentMethod.UNION_PAY, label: "銀聯" },
    { value: PaymentMethod.BANK_CASH_RECEIPTS, label: "銀行現金入數" },
    { value: PaymentMethod.BANK_AUTO_TRANSFER, label: "銀行自動轉賬" },
    { value: PaymentMethod.DELAY_PAYMENT, label: "延遲付款" },
    { value: PaymentMethod.OTHER, label: "其他" },
  ],
  paymentFrequency: [
    { value: PaymentFrequency.SinglePayment, label: "躉繳" },
    { value: PaymentFrequency.Annually, label: "年繳" },
    { value: PaymentFrequency.SemiAnnually, label: "半年繳" },
    { value: PaymentFrequency.Quarterly, label: "季繳" },
    { value: PaymentFrequency.Monthly, label: "月繳" },
  ],
  dividendDistributionMethod: [
    { value: DividendDistributionMethod.InterestAccumulation, label: "積存生息" },
    { value: DividendDistributionMethod.ToBePaidInCash, label: "現金支付" },
    { value: DividendDistributionMethod.ToReducePremium, label: "扣除保費" },
    { value: DividendDistributionMethod.ToPurchasePaidUpAddition, label: "購買付清保險" },
  ],
  familyRelationShip: [
    { value: FamilyMemberRelationship.Unknown, label: "未知" },
    { value: FamilyMemberRelationship.Father, label: "父親" },
    { value: FamilyMemberRelationship.Mother, label: "母親" },
    { value: FamilyMemberRelationship.Son, label: "兒子" },
    { value: FamilyMemberRelationship.Daughter, label: "女兒" },
  ],
} satisfies Record<string, DictOption[]>;

/** 統一格式化通用字段 */
export function formatCommonDict(field: keyof typeof COMMON_DICTS, value: any, fallback = "-") {
  return getDictLabel(COMMON_DICTS[field], value as any, fallback);
}

/**
 * 統一格式化元數據字段
 * @param code 元數據代碼（如 "ID_TYPE", "SALUTATION" 等）
 * @param value 字段值
 * @param fallback 未找到時的默認值
 * @returns 格式化後的顯示文本
 */
export function formatMetadataDict(code: string, value: any, fallback = "-"): string {
  if (value === undefined || value === null || value === "") return fallback;

  const metadata = getSelectMetadataByCode(code);
  if (metadata?.items && metadata.items.length > 0) {
    const dictOptions = metadata.items.map((item) => ({
      label: item.name || "",
      value: item.key || "",
    }));
    return getDictLabel(dictOptions, value, fallback);
  }

  return fallback;
}

/**
 * 獲取標籤文本 - 支持元數據代碼或 fallback 字典
 *
 * 使用示例：
 * ```typescript
 * // 使用元數據代碼
 * getMetadataLabel("ID_TYPE", "HONGKONG_ID") // => "香港身份證"
 *
 * // 使用國家代碼（特殊處理）
 * getMetadataLabel("COUNTRY", "HK") // => "香港"（從 useCountryStore 獲取）
 *
 * // 使用 fallback 字典
 * getMetadataLabel(null, "MALE", COMMON_DICTS.gender) // => "男"
 *
 * // 元數據未找到時使用 fallback
 * getMetadataLabel("ID_TYPE", "CUSTOM_ID", COMMON_DICTS.idType) // => fallback dict result
 * ```
 *
 * @param code 元數據代碼（如 "ID_TYPE"），可為 null
 * @param value 字段值
 * @param fallbackDict 備用字典（當元數據未找到時使用）
 * @param defaultValue 默認值（當都未找到時返回）
 * @returns 格式化後的顯示文本
 */
export function getMetadataLabel(code: string | null, value: any, fallbackDict?: DictOption[] | DictMap, defaultValue = "-"): string {
  if (value === undefined || value === null || value === "") return defaultValue;

  // 特殊處理：COUNTRY 代碼使用國家數據提供者
  if (code === "COUNTRY") {
    const countries = getCountries();
    if (countries.length > 0) {
      const country = countries.find((c) => c.countryCode === value);
      if (country) {
        return country.cname || country.name || value;
      }
    }
  }

  // 優先使用元數據
  if (code) {
    const metadata = getSelectMetadataByCode(code);
    if (metadata?.items && metadata.items.length > 0) {
      const found = metadata.items.find((item) => item.key === value);
      if (found) return found.name || found.key || defaultValue;
    }
  }

  // 使用 fallback 字典
  if (fallbackDict) {
    return getDictLabel(fallbackDict, value, defaultValue);
  }

  // 都未找到，返回原值或默認值
  return String(value) || defaultValue;
}
